import { useState, useEffect, useCallback } from 'react';
import { 
  Server, Search, Filter, MoreVertical, Eye, Play, Square, 
  RotateCw, Trash2, X, User, MapPin, Cpu, HardDrive, 
  Activity, MessageSquare, Clock, ExternalLink
} from 'lucide-react';
import { api } from '@/lib/api-client';
import { Link } from 'react-router-dom';

export default function AdminInstances() {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionDropdown, setActionDropdown] = useState(null);

  const fetchInstances = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (tierFilter) params.append('tier', tierFilter);
      
      const data = await api.get(`/api/auth/admin/instances/?${params.toString()}`);
      setInstances(data);
    } catch (err) {
      console.error('Failed to fetch instances:', err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, tierFilter]);

  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  const handleViewInstance = async (instanceId) => {
    try {
      const data = await api.get(`/api/auth/admin/instances/${instanceId}/`);
      setSelectedInstance(data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Failed to fetch instance:', err);
    }
    setActionDropdown(null);
  };

  const handleInstanceAction = async (instanceId, action) => {
    const actionLabels = { start: 'start', stop: 'stop', restart: 'restart' };
    if (!window.confirm(`Are you sure you want to ${actionLabels[action]} this instance?`)) return;
    
    try {
      await api.post(`/api/auth/admin/instances/${instanceId}/${action}/`);
      fetchInstances();
    } catch (err) {
      alert(err.message || 'Failed to perform action');
    }
    setActionDropdown(null);
  };

  const handleDeleteInstance = async (instance) => {
    if (!window.confirm(`Are you sure you want to delete "${instance.name}"? This action cannot be undone.`)) return;
    
    try {
      await api.delete(`/api/auth/admin/instances/${instance.id}/delete/`);
      fetchInstances();
    } catch (err) {
      alert(err.message || 'Failed to delete instance');
    }
    setActionDropdown(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      running: 'bg-emerald-500/20 text-emerald-400',
      stopped: 'bg-zinc-500/20 text-zinc-400',
      provisioning: 'bg-blue-500/20 text-blue-400',
      error: 'bg-red-500/20 text-red-400',
      installing: 'bg-amber-500/20 text-amber-400',
    };
    return <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${styles[status] || styles.stopped}`}>{status}</span>;
  };

  const getTierBadge = (tier) => {
    const styles = {
      starter: 'bg-blue-500/20 text-blue-400',
      professional: 'bg-emerald-500/20 text-emerald-400',
      enterprise: 'bg-purple-500/20 text-purple-400',
    };
    return <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${styles[tier] || styles.starter}`}>{tier}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Server className="text-emerald-400" /> All Instances
        </h1>
        <p className="text-zinc-500 text-sm mt-1">View and manage all user instances across the platform</p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <p className="text-zinc-400 text-sm">Total Instances</p>
          <p className="text-2xl font-bold text-white mt-1">{instances.length}</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <p className="text-zinc-400 text-sm">Running</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">{instances.filter(i => i.status === 'running').length}</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <p className="text-zinc-400 text-sm">Stopped</p>
          <p className="text-2xl font-bold text-zinc-400 mt-1">{instances.filter(i => i.status === 'stopped').length}</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <p className="text-zinc-400 text-sm">Issues</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{instances.filter(i => i.status === 'error').length}</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, IP, or owner email..."
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${showFilters ? 'bg-emerald-500/10 border-emerald-500 text-emerald-400' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}
          >
            <Filter size={18} /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-zinc-800">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:border-emerald-500 focus:outline-none"
            >
              <option value="">All Status</option>
              <option value="running">Running</option>
              <option value="stopped">Stopped</option>
              <option value="provisioning">Provisioning</option>
              <option value="error">Error</option>
            </select>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:border-emerald-500 focus:outline-none"
            >
              <option value="">All Tiers</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
            {(statusFilter || tierFilter) && (
              <button
                onClick={() => { setStatusFilter(''); setTierFilter(''); }}
                className="text-zinc-500 hover:text-white text-sm flex items-center gap-1"
              >
                <X size={14} /> Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Instances Table */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-800/50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Instance</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Owner</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Tier</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Resources</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Activity</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-zinc-500">
                    <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
                    <p className="mt-2">Loading instances...</p>
                  </td>
                </tr>
              ) : instances.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-zinc-500">
                    <Server size={32} className="mx-auto mb-2 opacity-50" />
                    No instances found
                  </td>
                </tr>
              ) : (
                instances.map((instance) => (
                  <tr key={instance.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${instance.status === 'running' ? 'bg-emerald-500/20' : 'bg-zinc-700'}`}>
                          <Server size={18} className={instance.status === 'running' ? 'text-emerald-400' : 'text-zinc-400'} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{instance.name}</p>
                          <p className="text-zinc-500 text-sm flex items-center gap-1">
                            <MapPin size={12} /> {instance.region} • {instance.ip || 'No IP'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-zinc-500" />
                        <div>
                          <p className="text-white text-sm">{instance.user?.full_name || 'No name'}</p>
                          <p className="text-zinc-500 text-xs">{instance.user?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">{getStatusBadge(instance.status)}</td>
                    <td className="px-4 py-4">{getTierBadge(instance.tier)}</td>
                    <td className="px-4 py-4">
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-zinc-400">
                          <Cpu size={12} /> {instance.cpu_usage}%
                          <span className="w-16 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                            <span className="block h-full bg-emerald-500" style={{ width: `${instance.cpu_usage}%` }} />
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                          <HardDrive size={12} /> {instance.ram_usage}%
                          <span className="w-16 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                            <span className="block h-full bg-cyan-500" style={{ width: `${instance.ram_usage}%` }} />
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1 text-sm text-zinc-400">
                        <div className="flex items-center gap-1">
                          <MessageSquare size={12} /> {instance.messages_count || 0} msgs
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={12} /> {instance.uptime || '0d 0h'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="relative flex justify-end">
                        <button
                          onClick={() => setActionDropdown(actionDropdown === instance.id ? null : instance.id)}
                          className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                        >
                          <MoreVertical size={16} className="text-zinc-400" />
                        </button>
                        {actionDropdown === instance.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-10 py-1">
                            <button
                              onClick={() => handleViewInstance(instance.id)}
                              className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                            >
                              <Eye size={14} /> View Details
                            </button>
                            {instance.status !== 'running' && (
                              <button
                                onClick={() => handleInstanceAction(instance.id, 'start')}
                                className="w-full px-4 py-2 text-left text-sm text-emerald-400 hover:bg-zinc-700 flex items-center gap-2"
                              >
                                <Play size={14} /> Start Instance
                              </button>
                            )}
                            {instance.status === 'running' && (
                              <button
                                onClick={() => handleInstanceAction(instance.id, 'stop')}
                                className="w-full px-4 py-2 text-left text-sm text-amber-400 hover:bg-zinc-700 flex items-center gap-2"
                              >
                                <Square size={14} /> Stop Instance
                              </button>
                            )}
                            <button
                              onClick={() => handleInstanceAction(instance.id, 'restart')}
                              className="w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                            >
                              <RotateCw size={14} /> Restart Instance
                            </button>
                            <div className="border-t border-zinc-700 my-1" />
                            <button
                              onClick={() => handleDeleteInstance(instance)}
                              className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-zinc-700 flex items-center gap-2"
                            >
                              <Trash2 size={14} /> Delete Instance
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Instance Detail Modal */}
      {showDetailModal && selectedInstance && (
        <InstanceDetailModal 
          instance={selectedInstance} 
          onClose={() => { setShowDetailModal(false); setSelectedInstance(null); }}
          onAction={(action) => {
            handleInstanceAction(selectedInstance.id, action);
            setShowDetailModal(false);
          }}
        />
      )}
    </div>
  );
}

// Instance Detail Modal
function InstanceDetailModal({ instance, onClose, onAction }) {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${instance.status === 'running' ? 'bg-emerald-500/20' : 'bg-zinc-700'}`}>
              <Server size={24} className={instance.status === 'running' ? 'text-emerald-400' : 'text-zinc-400'} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{instance.name}</h2>
              <p className="text-zinc-400 text-sm">{instance.ip || 'No IP assigned'} • {instance.region}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
            <X size={20} className="text-zinc-400" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick Actions */}
          <div className="flex gap-2">
            {instance.status !== 'running' && (
              <button onClick={() => onAction('start')} className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 flex items-center gap-2">
                <Play size={16} /> Start
              </button>
            )}
            {instance.status === 'running' && (
              <button onClick={() => onAction('stop')} className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg hover:bg-amber-500/30 flex items-center gap-2">
                <Square size={16} /> Stop
              </button>
            )}
            <button onClick={() => onAction('restart')} className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 flex items-center gap-2">
              <RotateCw size={16} /> Restart
            </button>
          </div>

          {/* Owner Info */}
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-zinc-400 mb-3">Owner</h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                {(instance.user?.full_name || instance.user?.email || 'U')[0].toUpperCase()}
              </div>
              <div>
                <p className="text-white font-medium">{instance.user?.full_name || 'No name'}</p>
                <p className="text-zinc-400 text-sm">{instance.user?.email} • {instance.user?.plan} plan</p>
              </div>
            </div>
          </div>

          {/* Instance Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-zinc-800/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Configuration</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">Status</span><span className="text-white capitalize">{instance.status}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Tier</span><span className="text-white capitalize">{instance.tier}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Region</span><span className="text-white">{instance.region}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Model</span><span className="text-white">{instance.model_name}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Channel</span><span className="text-white capitalize">{instance.channel}</span></div>
              </div>
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Resources</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">CPU Cores</span><span className="text-white">{instance.cpu_cores}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">RAM</span><span className="text-white">{instance.ram_gb} GB</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Storage</span><span className="text-white">{instance.disk_gb} GB</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">Bandwidth</span><span className="text-white">{instance.bandwidth_gb} GB</span></div>
              </div>
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Usage</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-400">CPU</span><span className="text-white">{instance.cpu_usage}%</span>
                  </div>
                  <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${instance.cpu_usage}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-400">RAM</span><span className="text-white">{instance.ram_usage}%</span>
                  </div>
                  <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500" style={{ width: `${instance.ram_usage}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-400">Disk</span><span className="text-white">{instance.disk_usage}%</span>
                  </div>
                  <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500" style={{ width: `${instance.disk_usage}%` }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Access Credentials</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-400">RDP Username</span><span className="text-white font-mono">{instance.rdp_username}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">RDP Password</span><span className="text-white font-mono">{instance.rdp_password || '••••••••'}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">RDP Port</span><span className="text-white">{instance.rdp_port}</span></div>
                <div className="flex justify-between"><span className="text-zinc-400">SSH Port</span><span className="text-white">{instance.ssh_port}</span></div>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-zinc-400 mb-3">Timestamps</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-zinc-400">Created:</span> <span className="text-white">{new Date(instance.created_at).toLocaleString()}</span></div>
              <div><span className="text-zinc-400">Updated:</span> <span className="text-white">{new Date(instance.updated_at).toLocaleString()}</span></div>
              {instance.last_started_at && <div><span className="text-zinc-400">Last Started:</span> <span className="text-white">{new Date(instance.last_started_at).toLocaleString()}</span></div>}
              {instance.last_stopped_at && <div><span className="text-zinc-400">Last Stopped:</span> <span className="text-white">{new Date(instance.last_stopped_at).toLocaleString()}</span></div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
